<template>
  <div class="nationality-profile">
    <div class="nationality-profile__header">
      <h4 class="text-left nationality-profile__title">{{ schoolShortName }} nationality profile</h4>
      <b-button v-if="!isEmpty" size="sm" variant="outline-secondary" class="nationality-profile__download" @click="downloadNationalityProfilePdf">
        <i class="fa fa-download"></i> Download PDF
      </b-button>
    </div>
    <p class="nationality-profile__subtitle">A high-level view of the nationalities represented at {{ schoolName }}.</p>

    <empty-list v-if="isEmpty" />

    <div v-if="!isEmpty" class="nationality-profile__body">
      <div class="nationality-profile__summary">
        <donut-chart :segments="donutSegments" caption="students" :highlighted="hoveredNationality" @hover="hoveredNationality = $event" />

        <div class="nationality-profile__count-card">
          <span class="nationality-profile__count-number">{{ nationalities.length }}</span>
          different nationalities
        </div>
      </div>

      <nationality-list
        class="nationality-profile__list"
        :nationalities="nationalities"
        :highlighted="hoveredNationality"
        @hover="hoveredNationality = $event"
      />
    </div>
  </div>
</template>

<script>
import Cookies from 'js-cookie'
import EmptyList from '../EmptyList.vue'
import DonutChart from '../charts/DonutChart.vue'
import NationalityList from './NationalityList.vue'
import nationalityProfilePdfMixin from './mixins/nationalityProfilePdf'

const UNSPECIFIED_NATIONALITY = 'Not specified'
// Chart.js default fill, shown by the existing pie when a nationality has no palette color
const DEFAULT_CHART_COLOR = 'rgba(0, 0, 0, 0.1)'

export default {
  name: 'NationalityProfileWidget',
  components: { EmptyList, DonutChart, NationalityList },
  mixins: [nationalityProfilePdfMixin],
  props: {
    // Same chart data as the Student Nationality pie on the home page, so each nationality keeps its color
    chartData: {
      type: Object,
      default: () => ({ labels: [], datasets: [{ data: [], backgroundColor: [], hoverBackgroundColor: [] }] }),
    },
  },
  data() {
    return {
      schoolName: Cookies.get('userSchoolNameSession') || 'EtonHouse',
      hoveredNationality: null,
    }
  },
  computed: {
    schoolShortName() {
      return this.schoolName.replace(/^EtonHouse\s+/i, '')
    },
    totalStudents() {
      return this.chartData.datasets[0].data.reduce((total, count) => total + count, 0)
    },
    isEmpty() {
      return this.totalStudents < 1
    },
    nationalities() {
      const { labels, datasets } = this.chartData
      const { data, backgroundColor, hoverBackgroundColor } = datasets[0]

      return labels
        .map((label, index) => ({
          name: (label || '').trim() || UNSPECIFIED_NATIONALITY,
          count: data[index],
          percent: (data[index] / this.totalStudents) * 100,
          color: backgroundColor[index] || DEFAULT_CHART_COLOR,
          hoverColor: hoverBackgroundColor[index] || backgroundColor[index] || DEFAULT_CHART_COLOR,
        }))
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    },
    donutSegments() {
      return this.nationalities.map(nationality => ({
        label: nationality.name,
        value: nationality.count,
        color: nationality.color,
        hoverColor: nationality.hoverColor,
      }))
    },
  },
}
</script>

<style scoped>
.nationality-profile__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.nationality-profile__download {
  flex-shrink: 0;
}

.nationality-profile__title {
  font-size: 20px;
  font-family: Georgia, 'Times New Roman', serif;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 4px;
}

.nationality-profile__subtitle {
  text-align: left;
  font-size: 12px;
  color: #6b6b6b;
  margin-bottom: 16px;
}

.nationality-profile__body {
  display: flex;
  align-items: center;
  gap: 40px;
}

.nationality-profile__summary {
  flex: 0 0 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.nationality-profile__count-card {
  margin-top: 16px;
  width: 100%;
  padding: 10px 12px;
  background: #f6f5f3;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #1a1a1a;
  text-align: center;
}

.nationality-profile__count-number {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 20px;
  font-weight: 700;
  color: #9b1b30;
  margin-right: 4px;
}

.nationality-profile__list {
  flex: 1;
  min-width: 0;
}

@media (max-width: 991px) {
  .nationality-profile__body {
    flex-direction: column;
    align-items: stretch;
  }

  .nationality-profile__summary {
    flex-basis: auto;
    align-self: center;
    width: 240px;
    max-width: 100%;
  }
}
</style>
